import { useMemo } from "react";
import { LuPanelRightOpen } from "react-icons/lu";
import { FaRegUser } from "react-icons/fa";
import PatientFormFields from "./components/PatientFormFields";
import PatientAllergies from "./components/PatientAllergies";
import PatientVitals from "./components/PatientVitals";
import useTitanApp from "../../../../../../hooks/useTitanApp";
import {
  setFirstPatientPanelTitle,
  setTogglePatientPanelOne,
  setCurrentShowPatientDetailsEditModal,
  setSelectedPatientConsultation,
} from "../../../../../../dispatchers/patientDispatchers";
import PatientRelatedConditions from "./components/PatientRelatedConditions";
import PatientMedications from "./components/PatientMedications";
import PatientPreviousConsultations from "./components/PatientPreviousConsultations";
import { Tooltip } from "@mui/material";
import EditSVGLight from "../../../../../../assets/images/edit-pencil-dark.svg";
import EditSVGDark from "../../../../../../assets/images/edit-pencil-light.svg";
import { PreviousConsultations } from "../../../../../../components/PatientDashboardV2/components/PreviousConsultations";
import { NhsServicesMenu } from "../../../../../../components/PatientDashboardV2/components/NhsServicesMenu";
import {
  fetchConsultationTimeline,
  useConsultationsState,
  mapPatientConsultationsToUI,
} from "../../../../../../lib/dashboard/consultationsData";
import { ConsultationActiveTreatmentPlans } from "./components/ConsultationActiveTreatmentPlans";
import { mapAppointments } from "../../../../../../lib/dashboard/appointmentsData";
import { mapPatientVitalsToDashboard } from "../../../../../../lib/dashboard/vitalsData";
import { VitalsSection } from "../../../../../PatientDashboardV2/components/VitalsSection";
import { usePatientVitalsState } from "../../../../../../lib/dashboard/vitalsData";
import { checkUserPermissions } from "../../../../../../utils/checkUserPermissions";
import {
  accessTitanverseConsultationView,
  accessTitanverseConsultationUpsert,
} from "../../../../../../data/constantStrings";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "../../../../../../dispatchers/appDispatchers";
export default function PatientProfile() {
  const { appState, patientState, treatmentPlanState, pharmacyServiceState } =
    useTitanApp();
  const { allPharmacies } = pharmacyServiceState || {};
  const {
    patientActiveTreatmentPlans,
    activeTreatmentPlansLoader,
    linkedTreatmentPlanId,
    linkedTreatmentPlanAppointmentId,
  } = treatmentPlanState || {};
  const { darkMode } = appState || {};
  const { currentPharmacyId } = appState || {};
  const {
    selectedPatient,
    patientAppointmentList,
    patientConsultations,
    patientVitals,
  } = patientState || {};
  const { setSelectedConsultation } = useConsultationsState();
  const {
    DashboardPatientVitals,
    addNewPatientVital,
    selectedPatientDetails,
    generateHistoricalData,
    patientID,
  } = usePatientVitalsState();

  const matchedPharmacy =
    allPharmacies?.find(
      (pharmacy) =>
        (pharmacy?.teamPharmacyID || pharmacy?.pharmacyID) === currentPharmacyId,
    ) || null;

  const requestingOdsCode =
    matchedPharmacy?.odsCode ||
    matchedPharmacy?.ODSCode ||
    matchedPharmacy?.ods ||
    "";

  const handleSaveVitals = async (newVital) => {
    await addNewPatientVital(
      newVital,
      patientID,
      selectedPatientDetails?.patientName || "",
    );
  };

  const mappedAppointments = useMemo(
    () => mapAppointments(patientAppointmentList || []),
    [patientAppointmentList],
  );
  const mappedConsultations = useMemo(
    () => mapPatientConsultationsToUI(patientConsultations || []),
    [patientConsultations],
  );
  const mappedVitals = useMemo(
    () => mapPatientVitalsToDashboard(patientVitals),
    [patientVitals],
  );
  const handleFirstPatientPanelCollapsing = () => {
    setTogglePatientPanelOne(true);
    setFirstPatientPanelTitle("Profile");
  };

  async function handleEditPatient() {
    setCurrentShowPatientDetailsEditModal(true);
  }
  const hasConsultationPermission =
    checkUserPermissions(accessTitanverseConsultationUpsert) ||
    checkUserPermissions(accessTitanverseConsultationView);
  return (
    <div className="pc-tabbed-content patient-profile-wrapper">
      <div className="patient-profile-header">
        <div className="name-collapsible">
          <LuPanelRightOpen
            className="collapse-icon"
            color={darkMode ? "#fff" : "#0a0a0a"}
            onClick={handleFirstPatientPanelCollapsing}
          />

          <div className="patient-name">
            <FaRegUser color={darkMode ? "#bdbdbd" : "#0a0a0a"} size={16} />
            <h5 className="font-medium">
              {selectedPatient
                ?.split(" ")
                .filter((word) => word !== "null" && word !== "undefined")
                .join(" ")}
            </h5>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <NhsServicesMenu
            nhsNumber={selectedPatientDetails?.nhsNumber}
            requestingOdsCode={requestingOdsCode}
          />
          <Tooltip title="Edit Patient" arrow>
            <div
              className="patient-profile-edit-icon"
              onClick={handleEditPatient}
              role="button"
              aria-label="Edit Patient"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleEditPatient();
              }}
            >
              {darkMode ? <EditSVGDark /> : <EditSVGLight />}
            </div>
          </Tooltip>
        </div>
      </div>
      <PatientFormFields />

      {/* patient treatment plan component here  */}
      {patientActiveTreatmentPlans?.length > 0 && (
        <ConsultationActiveTreatmentPlans
          treatmentPlans={patientActiveTreatmentPlans}
          isDarkMode={darkMode}
          isLoading={activeTreatmentPlansLoader}
          linkedTreatmentPlanId={linkedTreatmentPlanId}
          linkedTreatmentPlanAppointmentId={linkedTreatmentPlanAppointmentId}
          appointments={mappedAppointments}
          consultations={mappedConsultations}
          vitals={mappedVitals}
        />
      )}

      <PatientAllergies />

      <PatientRelatedConditions />
      <PatientMedications />

      {/* <PatientVitals /> */}
      <VitalsSection
        vitals={DashboardPatientVitals}
        isDarkMode={darkMode}
        onSaveVitals={hasConsultationPermission ? handleSaveVitals : undefined}
        hasConsultationPermission={hasConsultationPermission}
        onPermissionDenied={() => {
          setShowPermissionModal(true);
          setShowPermissionMessage(
            "You don't have permission to add or edit vitals.",
          );
        }}
        compact={true}
      />
      <PreviousConsultations
        isDarkMode={darkMode}
        onSelectConsultation={(consultation) => {
          // setSelectedPatientConsultation(consultation);
          fetchConsultationTimeline(consultation.consultationId);
          setSelectedConsultation(consultation);
        }}
      />
    </div>
  );
}

